import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, AlertCircle, CheckCircle2, Clock, Search, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { listIssues, getStats, listUploads } from '../api';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

const DashboardPage = ({ currentUploadId: propUploadId }) => {
  const { uploadId: paramUploadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [uploadId, setUploadId] = useState(paramUploadId || propUploadId || '');

  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({});
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [density, setDensity] = useState('comfortable');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
  });

  useEffect(() => {
    loadData();
  }, [uploadId]);

  useEffect(() => {
    const fetchUploads = async () => {
      try {
        const data = await listUploads();
        setUploads(Array.isArray(data) ? data.filter(u => u.status === 'completed') : []);
      } catch (err) {
        console.error('Error loading uploads:', err);
      }
    };
    fetchUploads();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = uploadId ? { upload_id: uploadId } : {};
      const [issuesResponse, statsData] = await Promise.all([
        listIssues(params),
        getStats(uploadId || null),
      ]);
      // Handle both paginated {items: [...]} and legacy array responses
      const issuesData = issuesResponse?.items ?? issuesResponse;
      setIssues(Array.isArray(issuesData) ? issuesData : []);
      setStats(statsData);
    } catch (err) {
      toast.error('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async () => {
    try {
      const params = {
        ...(uploadId ? { upload_id: uploadId } : {}),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v && v.trim())
        ),
      };
      const response = await listIssues(params);
      const data = response?.items ?? response;
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to apply filters');
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      handleFilterChange();
    }, 300);
    return () => clearTimeout(debounce);
  }, [filters]);

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-[hsl(0_84%_96%)] text-[hsl(0_72%_35%)] border-[hsl(0_70%_85%)]',
      high: 'bg-[hsl(43_100%_95%)] text-[hsl(30_90%_30%)] border-[hsl(43_90%_85%)]',
      medium: 'bg-[hsl(199_70%_95%)] text-[hsl(199_89%_28%)] border-[hsl(199_60%_85%)]',
      low: 'bg-[hsl(210_20%_96%)] text-[hsl(215_16%_40%)] border-[hsl(214_20%_88%)]',
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-[hsl(199_70%_95%)] text-[hsl(199_89%_28%)]',
      triaged: 'bg-[hsl(43_100%_95%)] text-[hsl(30_90%_30%)]',
      in_progress: 'bg-[hsl(186_45%_92%)] text-[hsl(199_89%_20%)]',
      resolved: 'bg-[hsl(150_45%_93%)] text-[hsl(160_60%_28%)]',
      wont_fix: 'bg-[hsl(210_18%_94%)] text-[hsl(215_16%_40%)]',
    };
    return colors[status] || colors.open;
  };

  const topIssues = issues.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="border-b bg-card">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="font-display text-2xl font-bold" data-testid="dashboard-page-title">Issues Dashboard</h1>
              <p className="text-sm text-muted-foreground">Recurring issues from WhatsApp chat</p>
            </div>
            <ToggleGroup type="single" value={density} onValueChange={setDensity}>
              <ToggleGroupItem value="compact" className="text-xs">Compact</ToggleGroupItem>
              <ToggleGroupItem value="comfortable" className="text-xs">Comfortable</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPI Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Issues</p>
                  <p className="font-display text-3xl font-bold tabular-nums mt-2" data-testid="dashboard-kpi-total-issues">
                    {stats.total_issues || 0}
                  </p>
                </div>
                <TrendingUp className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Open</p>
                  <p className="font-display text-3xl font-bold tabular-nums mt-2">
                    {stats.open_issues || 0}
                  </p>
                </div>
                <AlertCircle className="w-5 h-5 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Resolved</p>
                  <p className="font-display text-3xl font-bold tabular-nums mt-2">
                    {stats.resolved_issues || 0}
                  </p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Most Repeated</p>
                  <p className="font-display text-3xl font-bold tabular-nums mt-2">
                    {stats.most_repeated_count || 0}
                  </p>
                </div>
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Last Processed</p>
                  <p className="text-xs text-muted-foreground mt-2 font-mono">
                    {stats.last_processed
                      ? new Date(stats.last_processed).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <Clock className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Priority Stack */}
        {topIssues.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="text-lg font-semibold mb-4">Top Priority Issues</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topIssues.map((issue, idx) => (
                <Card
                  key={issue.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/issue/${issue.id}`)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-tight flex-1">
                        {issue.title}
                      </CardTitle>
                      <span className="font-display text-2xl font-bold text-primary tabular-nums">
                        {issue.count}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={`text-xs ${getPriorityColor(issue.priority)} border`}>
                        {issue.priority}
                      </Badge>
                      <Badge className={`text-xs ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {issue.tags?.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">
                      Last seen: {new Date(issue.last_seen).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search issues..."
              className="pl-9"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="dashboard-filter-search-input"
            />
          </div>
          <Select
            value={uploadId}
            onValueChange={(value) => setUploadId(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-[200px]" data-testid="dashboard-filter-upload-select">
              <SelectValue placeholder="All Uploads" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Uploads</SelectItem>
              {uploads.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.filename ? u.filename.substring(0, 30) : u.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status}
            onValueChange={(value) => setFilters({ ...filters, status: value })}
          >
            <SelectTrigger className="w-[150px]" data-testid="dashboard-filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="triaged">Triaged</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.priority}
            onValueChange={(value) => setFilters({ ...filters, priority: value })}
          >
            <SelectTrigger className="w-[150px]" data-testid="dashboard-filter-priority-select">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All Priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Issues Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="issues-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="w-[50px]">Count</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="w-[120px]">Priority</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[140px]">Last Seen</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No issues found
                    </TableCell>
                  </TableRow>
                ) : (
                  issues.map((issue) => (
                    <TableRow
                      key={issue.id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/issue/${issue.id}`)}
                      data-testid="issues-table-row"
                    >
                      <TableCell className="font-medium tabular-nums">
                        <span className="font-display text-lg text-primary">
                          {issue.count}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{issue.title}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {issue.summary}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {issue.tags?.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {issue.tags?.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{issue.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getPriorityColor(issue.priority)} border`}>
                          {issue.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getStatusColor(issue.status)}`}>
                          {issue.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground font-mono">
                        {new Date(issue.last_seen).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;